import { Injectable, inject, signal } from "@angular/core";
import { Pays, Right } from "@models/user.model";
import { CardCateg, CardType, Lang, MarketPlace, Rarity, Variante } from '@models/card.model';
import { Api } from '@services/api.service';
import { of, tap, shareReplay, Observable, forkJoin, share, map } from "rxjs";
import { toSignal } from "@angular/core/rxjs-interop";
import { CardCondition } from "@models/binder.model";

@Injectable({
	providedIn: 'root'
})
export class Global{
	//dépendences
	private readonly api=inject(Api);
	
	//init
	private readonly init$=this.global$();

	//pays
	private pays=signal<Pays[]>(null);
	private paysObs: Observable<Pays[]>;

	//langs
	private langs=signal<Lang[]>(null);
	private langObs: Observable<Lang[]>;

	//rights
	private rights=signal<Right[]>(null);
	private rightObs: Observable<Right[]>;

	//variantes
	private variantes=signal<Variante[]>(null);
	private varObs: Observable<Variante[]>;

	//marketplace
	private marketplaces=signal<MarketPlace[]>(null);
	private marketObs: Observable<MarketPlace[]>;

	//card categs
	private readonly categs$=this.getCategs$();
	private readonly categs=toSignal(this.categs$, {initialValue: null});

	//card properties
	private readonly conditions=signal<CardCondition[]>([]);
	private readonly rarities=signal<Rarity[]>([]);
	private readonly types=signal<CardType[]>(null);

	getVersion(){
		return 'Bêta v0.0.1';
	}

	init(){
		return this.init$;
	}

	global$(){
		return this.api.get<InitApiResponse>('/globals/init')
			.pipe(
				shareReplay(1),
				tap(results => {
					this.pays.set(results.pays);
					this.langs.set(results.langs);
					this.variantes.set(results.variantes);
					this.marketplaces.set(results.marketpaces);
					this.conditions.set(results.conditions);
					this.rarities.set(results.rarities);
					this.types.set(results.types);
				})
			);
	}

	getPays(){
		if(this.pays())
			return of(this.pays());
		return this.getPaysObs();
	}

	private getPaysObs(){
		if(!this.paysObs){
			this.paysObs=this.api.get<Pays[]>('/pays')
				.pipe(
					shareReplay(1),
					tap(pays => this.pays.set(pays))
				);
		}
		return this.paysObs;
	}

	getLangs(){
		if(this.langs())
			return of(this.langs());
		return this.getLangObs();
	}

	private getLangObs(){
		if(!this.langObs){
			this.langObs=this.api.get<Lang[]>('/langs')
				.pipe(
					shareReplay(1),
					tap(langs => this.langs.set(langs))
				);
		}
		return this.langObs;
	}

	getRights(){
		if(this.rights())
			return of(this.rights());
		return this.getRightObs();
	}

	private getRightObs(){
		if(!this.rightObs){
			this.rightObs=this.api.get<Right[]>('/rights')
				.pipe(
					shareReplay(1),
					tap(rights => this.rights.set(rights))
				);
		}
		return this.rightObs;
	}

	getVariantes(){
		if(this.variantes())
			return of(this.variantes());
		return this.getVarObs();
	}

	private getVarObs(){
		if(!this.varObs){
			this.varObs=this.api.get<Variante[]>('/variantes')
				.pipe(
					shareReplay(1),
					tap(variantes => this.variantes.set(variantes))
				);
		}
		return this.varObs;
	}

	getMarket(){
		if(this.marketplaces())
			return of(this.marketplaces());
		return this.getMarketObs();
	}

	private getMarketObs(){
		if(!this.marketObs){
			this.marketObs=this.api.get<MarketPlace[]>('/marketplaces')
				.pipe(
					shareReplay(1),
					tap(markets => this.marketplaces.set(markets))
				);
		}
		return this.marketObs;
	}

	getCategs(){
		if(this.categs())
			return of(this.categs());
		else
			return this.categs$;
	}

	private getCategs$(){
		return this.api.get<CardCateg>('/globals/categs')
			.pipe(shareReplay(1)
			);
	}

	getCardConditions(){
		return of(this.conditions());
	}

	getTypes(){
		let obs: Observable<CardType[]>;
		if(!this.types()){
			obs=this.init().pipe(
				map(results => results.types)
			);
		}
		else{
			obs=of(this.types());
		}

		return obs;
	}

	getType(ref: string){
		return this.getTypes()
			.pipe(
				map(types => types.find(t => t.ref==ref))
			)
	}
}

type InitApiResponse={
	pays: Pays[];
	langs: Lang[];
	types: CardType[];
	variantes: Variante[];
	conditions: CardCondition[];
	marketpaces: MarketPlace[];
};